import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Factory } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Vendors = () => {
  const { t, API, canEdit, isAdmin } = useApp();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [newVendorName, setNewVendorName] = useState('');

  useEffect(() => {
    fetchVendors();
  }, []);

    const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      const sortedVendors = response.data.sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setVendors(sortedVendors);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };


  const handleCreate = async () => {
    if (!newVendorName.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }

    try {
      await axios.post(`${API}/vendors`, { name: newVendorName.trim() });
      toast.success(t('common.success'));
      setShowCreateDialog(false);
      setNewVendorName('');
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const handleDelete = async () => {
    if (!selectedVendor) return;

    try {
      await axios.delete(`${API}/vendors/${selectedVendor.id}`);
      toast.success(t('common.success'));
      setShowDeleteDialog(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('vendors.inUseWarning'));
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="vendors-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
            {t('vendors.title')}
          </h1>
          <p className="text-muted-foreground mt-2 font-mono text-xs">
            {vendors.length} {t('vendors.title').toLowerCase()}
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreateDialog(true)} className="font-display uppercase tracking-[0.16em] glow-amber" data-testid="add-vendor-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('vendors.addVendor')}
          </Button>
        )}
      </div>

      {/* Vendors Table */}
      <Card className="bezel">
        <div className="absolute top-0 inset-x-0 h-0.5 bg-primary/50" />
        <CardContent className="p-0">
          {vendors.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('vendors.name')}</TableHead>
                    <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('vendors.createdBy')}</TableHead>
                    <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('users.createdAt')}</TableHead>
                    {isAdmin && <TableHead className="eyebrow text-[10px] text-muted-foreground text-right">{t('common.actions')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((vendor) => (
                    <TableRow key={vendor.id} className="hover:bg-muted/40 transition-colors" data-testid={`vendor-row-${vendor.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-primary glow-amber">
                            <Factory className="w-5 h-5 text-primary" />
                          </div>
                          <span className="font-medium">{vendor.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{vendor.created_by}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{formatDate(vendor.created_at)}</TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => { setSelectedVendor(vendor); setShowDeleteDialog(true); }}
                            data-testid={`delete-vendor-${vendor.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Factory className="w-16 h-16 mx-auto text-primary/40 mb-4" />
              <p className="text-muted-foreground">{t('common.noResults')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-vendor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('vendors.addVendor')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('vendors.name')} *</Label>
              <Input
                value={newVendorName}
                onChange={(e) => setNewVendorName(e.target.value)}
                placeholder={t('vendors.name')}
                data-testid="vendor-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} data-testid="create-vendor-submit">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vendors.deleteVendor')}</AlertDialogTitle>
            <AlertDialogDescription>{t('vendors.confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive" data-testid="confirm-delete-vendor">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Vendors;
